/**
 * Instantiates the OpenTelemetry Web SDK for Honeycomb on the FareHarbor
 * waiver signing pages (`/waivers/` and `/waivers/link/<uuid>/`).
 *
 * This deliberately mirrors the attribute names, exporter behaviour and volume
 * controls of `website/static/js/services/honeycombWebSDK.js` (the dashboard /
 * legacy-embeds integration) so both frontends land in the same Honeycomb
 * dataset and are queryable the same way — but it is a standalone
 * implementation, because `waivers-website` is kept isolated from the main
 * `website` project.
 *
 * Two bugs are worth not re-introducing, both already fixed on the main site
 * in fc0a8f256dd and both re-appearing the first time this file was written
 * from an older copy: an `ignoreUrls` domain pattern that requires a leading
 * dot (drops every apex-domain API call, see `./otelIgnoreUrls`), and passing
 * the trace header filter through as strings instead of compiling it to
 * `RegExp` (kills trace propagation, see below).
 *
 * Config arrives on `window.honeycombConfiguration`, injected into
 * `src/index.html` by `server.charter.views.waivers_site`.
 *
 * The OTel packages are loaded via dynamic `import()` so they stay in a lazy
 * chunk and cost nothing in environments where frontend tracing is disabled.
 */

import { IGNORE_URLS_DEFAULT } from './otelIgnoreUrls';

/*
botProtection token is just to limit direct bot traffic to collector endpoints,
this value isn't supposed to be sensitive and will be visible within the source of our website.
*/
const botProtection = 'Basic ZmFyZWhhcmJvcjpkdW1teS1ib3QtcHJvdGVjdGlvbg==';

const {
  apiIngestKey = '',
  frontendServiceName = '',
  collectorEnabled = false,
  collectorUrl = '',
  traceHeaderFilter: rawTraceHeaderFilter,
  ignoreUrls: customIgnoreUrls,
} = window?.honeycombConfiguration || {};

/*
 * The server ships the filter as an array of regex *source strings* (JSON).
 * They must be compiled to `RegExp` here: `propagateTraceHeaderCorsUrls`
 * compares string patterns with `===` (see @opentelemetry/core `urlMatches`),
 * so leaving them as strings means trace headers are never propagated and
 * frontend spans never join their backend trace.
 */
const traceHeaderFilter =
  Array.isArray(rawTraceHeaderFilter) && rawTraceHeaderFilter.length > 0
    ? rawTraceHeaderFilter.map((src) => new RegExp(src))
    : [/\.?(fareharbor\.com|fareharbor\.engineering)(:\d+)?($|[/?#])/];

const configDefaults = {
  ignoreNetworkEvents: true,
  ignorePerformancePaintEvents: true,
  propagateTraceHeaderCorsUrls: traceHeaderFilter,
  webVitalsInstrumentationConfig: {
    enabled: true,
  },
  ignoreUrls: customIgnoreUrls ?? IGNORE_URLS_DEFAULT,
};

/*
 * Page shortname, read from the `?shortname=` query param that the
 * booking-keyed flow is opened with (see `WaiverLayout.vue`). The anonymous
 * standalone flow (`/waivers/link/<uuid>/`) has no shortname client-side —
 * the public API deliberately omits it — so the attribute is simply absent
 * there and `fh.frontend.page_type` is what separates the two flows.
 */
const pageShortname = (() => {
  try {
    return new URLSearchParams(window.location.search).get('shortname') || '';
  } catch {
    return '';
  }
})();

/*
 * Page type for this load. Only two waiver surfaces exist, both served by
 * `server.charter.views.waivers_site`: the booking-keyed form at `/waivers/`
 * and the anonymous standalone link at `/waivers/link/<uuid>/`. Values are
 * distinct from the main site's `fh.frontend.page_type` taxonomy (which has no
 * waiver entries), so the two never collide in Honeycomb.
 *
 * Resolved once at module load: unlike the dashboard, these are separate
 * document loads rather than client-side routes, so the value cannot change
 * during the page's lifetime.
 */
const pageType = /^\/waivers\/link\//.test(window.location.pathname)
  ? 'waiver-standalone'
  : 'waiver-booking';

const pageTypeProcessor = {
  onStart(span) {
    span.setAttribute('fh.frontend.page_type', pageType);
  },
  onEnd() {},
  shutdown() {
    return Promise.resolve();
  },
  forceFlush() {
    return Promise.resolve();
  },
};

/**
 * Company shortname a given request targets, parsed from its URL.
 *
 * The waiver app calls exactly four endpoints (see `src/components/utils.js`);
 * only the booking-keyed pair carries a shortname in the path. The standalone
 * pair is keyed solely by the public link uuid, so those spans carry no
 * `fh.request.shortname` — matching the fact that the standalone flow has no
 * shortname client-side at all.
 *
 * Operates on the path only (query/hash stripped) so relative and absolute
 * URLs match identically.
 */
const REQUEST_SHORTNAME_PATTERN = /^\/api\/v1\/companies\/([^/]+)\//;

function shortnameFromRequestUrl(url) {
  let path = url;
  if (/^https?:\/\//i.test(url)) {
    try {
      path = new URL(url).pathname;
    } catch {
      return '';
    }
  }
  const match = REQUEST_SHORTNAME_PATTERN.exec(path.split(/[?#]/)[0]);
  return match ? match[1] : '';
}

/*
 * Attaches company shortnames to every span, matching the main site:
 *
 *   - `fh.shortname`         – the company whose waiver page is open.
 *   - `fh.request.shortname` – the company a given API call targets, parsed
 *     from the request URL once the auto-instrumentations have populated
 *     `http.url` / `url.full`.
 *
 * The request-shortname pass uses `onEnding` (not `onEnd`) because the
 * upstream Span sets `_ended = true` *before* invoking `onEnd`, after which
 * `setAttribute` is a silent no-op. `onEnding` runs while the span is still
 * mutable.
 */
const shortnameProcessor = {
  onStart(span) {
    if (pageShortname) span.setAttribute('fh.shortname', pageShortname);
  },
  onEnding(span) {
    const attrs = (span && span.attributes) || {};
    const httpUrl =
      attrs['http.url'] || attrs['url.full'] || attrs['http.target'] || '';
    if (!httpUrl) return;
    const requestShortname = shortnameFromRequestUrl(String(httpUrl));
    if (requestShortname) {
      span.setAttribute('fh.request.shortname', requestShortname);
    }
  },
  onEnd() {},
  shutdown() {
    return Promise.resolve();
  },
  forceFlush() {
    return Promise.resolve();
  },
};

function resolveCollectorTracesUrl() {
  if (collectorUrl.endsWith('/v1/traces')) {
    return collectorUrl;
  }
  return `${collectorUrl.replace(/\/$/, '')}/v1/traces`;
}

const startHoneycombSDK = async () => {
  const [
    { HoneycombWebSDK },
    { getWebAutoInstrumentations },
    { UserInteractionInstrumentation },
    { OTLPExporterBase, createOtlpNetworkExportDelegate },
    { convertLegacyBrowserHttpOptions },
    { JsonTraceSerializer },
    { BatchSpanProcessor },
  ] = await Promise.all([
    import('@honeycombio/opentelemetry-web'),
    import('@opentelemetry/auto-instrumentations-web'),
    import('@opentelemetry/instrumentation-user-interaction'),
    import('@opentelemetry/otlp-exporter-base'),
    import('@opentelemetry/otlp-exporter-base/browser-http'),
    import('@opentelemetry/otlp-transformer'),
    import('@opentelemetry/sdk-trace-base'),
  ]);

  /**
   * Minimal OTLP/HTTP JSON trace exporter built on `fetch({ keepalive: true })`.
   *
   * Why custom: the default Honeycomb-web exporter is XHR-based, which the
   * browser cancels when the page unloads. Signing a waiver ends in a
   * navigation, so the XHR exporter loses exactly the spans we care about.
   * `fetch` with `keepalive` is allowed to outlive the document and keeps the
   * `Authorization` / `x-honeycomb-team` headers the collector requires —
   * something `navigator.sendBeacon` cannot do.
   */
  function createKeepaliveOtlpTraceExporter(config) {
    const options = convertLegacyBrowserHttpOptions(config, 'v1/traces', {
      'Content-Type': 'application/json',
    });

    const transport = {
      send(data) {
        const bodySize =
          typeof data === 'string' ? new Blob([data]).size : data.byteLength;
        const useKeepalive = bodySize <= 64000;

        try {
          return fetch(options.url, {
            method: 'POST',
            headers: options.headers(),
            body: data,
            mode: 'cors',
            credentials: 'omit',
            keepalive: useKeepalive,
          }).then(
            (res) =>
              res.ok
                ? { status: 'success' }
                : {
                    status: 'failure',
                    error: new Error(`OTLP export HTTP ${res.status}`),
                  },
            (err) => ({
              status: 'failure',
              error: err instanceof Error ? err : new Error(String(err)),
            }),
          );
        } catch (err) {
          return Promise.resolve({
            status: 'failure',
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      },
      shutdown() {},
    };

    return new OTLPExporterBase(
      createOtlpNetworkExportDelegate(options, JsonTraceSerializer, transport),
    );
  }

  const traceExporter = collectorEnabled
    ? createKeepaliveOtlpTraceExporter({
        url: resolveCollectorTracesUrl(),
        headers: { Authorization: botProtection },
      })
    : createKeepaliveOtlpTraceExporter({
        url: 'https://api.honeycomb.io/v1/traces',
        headers: {
          'x-honeycomb-team': apiIngestKey,
          Authorization: botProtection,
        },
      });

  const sdkOptions = {
    serviceName: frontendServiceName,
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-xml-http-request': configDefaults,
        '@opentelemetry/instrumentation-fetch': configDefaults,
        '@opentelemetry/instrumentation-document-load': configDefaults,
      }),
      // Kept to `error` only: `click` / `load` produce a span per interaction
      // and frontend ingest volume is already a standing concern
      // (see `otel.frontend_enabled` in fh-chart/values.yaml).
      new UserInteractionInstrumentation({
        eventNames: ['error'],
      }),
    ],
    spanProcessors: [
      pageTypeProcessor,
      shortnameProcessor,
      new BatchSpanProcessor(traceExporter, {
        maxExportBatchSize: 30, // Cap payload size to avoid Chrome's 64KB keepalive limit
        scheduledDelayMillis: 5000,
      }),
    ],
    localVisualizations: false, // For local UI visualisation. See Honeycomb docs for more.

    // Custom OTLP exporter above; omitting `traceExporter` stops HoneycombWebSDK
    // creating a second, default-configured BatchSpanProcessor.
    disableDefaultTraceExporter: true,

    resourceAttributes: {
      'fh.deployed_version': '1.0.0', // Bump when this file's behaviour changes.
    },
  };

  if (collectorEnabled) {
    sdkOptions.endpoint = collectorUrl;
    sdkOptions.skipOptionsValidation = true; // no apiKey is sent when exporting to a collector
  } else {
    sdkOptions.apiKey = apiIngestKey;
  }

  const sdk = new HoneycombWebSDK(sdkOptions);
  sdk.start();

  /*
   * Force-flush on `pagehide`, the bfcache-friendly replacement for `unload`.
   * Without it, spans already `.end()`ed but still queued in the
   * BatchSpanProcessor (waiting for the next ~5s tick) are discarded when the
   * page goes away — which is precisely what happens when a signed waiver
   * navigates away from the form.
   *
   * We deliberately do NOT also flush on `visibilitychange -> hidden`: that
   * fires on every tab switch and mobile backgrounding, and flushing that
   * aggressively races the SDK's own batching (open-telemetry/opentelemetry-js#5133).
   *
   * The listener must be synchronous from the browser's perspective, so the
   * promise is not awaited — both sync throws and async rejections are
   * swallowed. Telemetry must never break navigation.
   */
  const flushOnUnload = () => {
    try {
      const maybePromise = sdk.forceFlush();
      if (maybePromise && typeof maybePromise.catch === 'function') {
        maybePromise.catch(() => {
          // Swallow: telemetry failures must not break navigation.
        });
      }
    } catch {
      // Swallow: telemetry failures must not break navigation.
    }
  };

  window.addEventListener('pagehide', flushOnUnload);
};

if (window.honeycombConfiguration?.enabled) {
  startHoneycombSDK();
}
